<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { apiGet, apiPatch } from "../api";
import { useToast } from "../stores/toast";

interface Money { amount: number }
interface OrderLine { name: string; sku: string; size?: string; color?: string; unitPrice: Money; qty: number; discount?: Money; discountLabel?: string; lineTotal: Money }
interface ConversationMessage { role: "bot" | "user" | "event"; text: string }
interface OrderDetail {
  id: string; orderNumber: string; status: string; customer: { email: string; name?: string };
  items: OrderLine[]; subtotal: Money; shipping: Money; total: Money;
  delivery: { method: string }; shippingAddress?: Record<string, string>;
  conversation?: ConversationMessage[]; createdAt: string;
}

const STATUS: Record<string, { label: string; cls: string }> = {
  pending_payment: { label: "Te betalen", cls: "blue" },
  paid: { label: "Betaald", cls: "green" },
  nieuw: { label: "Nieuw", cls: "blue" },
  in_behandeling: { label: "In behandeling", cls: "amber" },
  verzonden: { label: "Verzonden", cls: "green" },
  retour: { label: "Retour", cls: "red" },
};
const euro = (c: number) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(c / 100);

const route = useRoute();
const router = useRouter();
const toast = useToast();
const id = computed(() => route.params.id as string);
const detail = ref<OrderDetail | null>(null);
const loading = ref(true);
const error = ref("");
const busy = ref(false);

async function markShipped() {
  if (!detail.value) return;
  busy.value = true;
  try {
    await apiPatch(`/orders/${detail.value.id}`, { status: "verzonden" });
    detail.value.status = "verzonden";
    toast.show("Gemarkeerd als verzonden");
  } catch (e) { error.value = `Mislukt: ${(e as Error).message}`; } finally { busy.value = false; }
}
onMounted(async () => {
  try { detail.value = await apiGet<OrderDetail>(`/orders/${id.value}`, { auth: true }); }
  catch (e) { error.value = String(e); } finally { loading.value = false; }
});
</script>

<template>
  <div>
    <p v-if="loading" class="muted">Laden…</p>
    <p v-else-if="error" class="err">{{ error }}</p>
    <template v-else-if="detail">
      <header class="dhead">
        <button class="back" @click="router.push('/bestellingen')">←</button>
        <h1 class="title">{{ detail.orderNumber }}</h1>
        <span class="badge" :class="STATUS[detail.status]?.cls">{{ STATUS[detail.status]?.label || detail.status }}</span>
        <button class="ship" :disabled="busy || detail.status === 'verzonden'" @click="markShipped">
          {{ detail.status === 'verzonden' ? 'Verzonden' : 'Markeer verzonden' }}
        </button>
      </header>
      <div class="dgrid">
        <div class="card">
          <h2 class="ct">Items</h2>
          <div v-for="(l, i) in detail.items" :key="i" class="line">
            <div>
              <strong>{{ l.name }}</strong>
              <div class="sub">{{ [l.size ? `maat ${l.size}` : '', l.color].filter(Boolean).join(' · ') }} · {{ l.qty }}×<span v-if="l.discountLabel" class="disc"> · {{ l.discountLabel }}</span></div>
            </div>
            <span>{{ euro(l.lineTotal.amount) }}</span>
          </div>
          <div class="tot"><span>Subtotaal</span><span>{{ euro(detail.subtotal.amount) }}</span></div>
          <div class="tot"><span>Bezorging</span><span>{{ detail.shipping.amount ? euro(detail.shipping.amount) : 'Gratis' }}</span></div>
          <div class="tot grand"><span>Totaal</span><span>{{ euro(detail.total.amount) }}</span></div>
        </div>
        <div class="side">
          <div class="card"><h2 class="ct">Klant</h2><strong>{{ detail.customer.name || '—' }}</strong><div class="sub">{{ detail.customer.email }}</div></div>
          <div class="card"><h2 class="ct">Bezorgadres</h2>
            <template v-if="detail.shippingAddress">{{ detail.shippingAddress.street }} {{ detail.shippingAddress.houseNumber }}<br />{{ detail.shippingAddress.postalCode }} {{ detail.shippingAddress.city }}</template>
            <span v-else class="sub">—</span>
          </div>
          <div class="card"><h2 class="ct">Bezorging</h2><span class="sub">{{ detail.delivery.method }}</span></div>
        </div>
      </div>

      <div v-if="detail.conversation?.length" class="card convo">
        <h2 class="ct">Gesprek tijdens bestellen</h2>
        <div class="chat">
          <div v-for="(m, i) in detail.conversation" :key="i" class="bubble" :class="m.role">{{ m.text }}</div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0; }
.muted { color: var(--muted); } .err { color: var(--red); }
.badge { font-size: 12px; font-weight: 600; padding: 3px 9px; border-radius: 999px; }
.badge.blue { color: #1d4ed8; background: #e5edfb; }
.badge.green { color: var(--green); background: var(--green-bg); }
.badge.amber { color: var(--amber); background: var(--amber-bg); }
.badge.red { color: var(--red); background: var(--red-bg); }
.dhead { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.back { font-size: 20px; border: 0; background: none; cursor: pointer; }
.ship { margin-left: auto; border: 0; border-radius: 999px; background: var(--coral); color: #fff; font-weight: 700; padding: 9px 16px; cursor: pointer; }
.ship:disabled { background: var(--green-bg); color: var(--green); cursor: default; }
.dgrid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 18px; }
.card { background: var(--surface); border: 1px solid var(--hairline); border-radius: 14px; padding: 18px; margin-bottom: 14px; }
.ct { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); margin: 0 0 12px; }
.line { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--hairline); }
.sub { color: var(--muted); font-size: 12px; }
.disc { color: var(--coral); }
.tot { display: flex; justify-content: space-between; padding: 4px 0; font-size: 14px; }
.tot.grand { font-weight: 700; font-size: 16px; }
.convo { max-width: 720px; }
.chat { display: flex; flex-direction: column; gap: 8px; }
.bubble { max-width: 75%; padding: 9px 13px; border-radius: 14px; font-size: 14px; line-height: 1.35; }
.bubble.bot { align-self: flex-start; background: var(--warm); border-bottom-left-radius: 4px; }
.bubble.user { align-self: flex-end; background: var(--coral); color: #fff; border-bottom-right-radius: 4px; }
.bubble.event { align-self: center; max-width: 90%; background: transparent; color: var(--muted); font-size: 12px; font-style: italic; padding: 2px 8px; }
</style>
